import React, { useRef, useEffect } from 'react';
import classNames from 'classnames';
import { VideoThumbnail } from './VideoThumbnail';
import styles from './styles.module.scss';

const ACTIVE_VIDEO_INDEX = 1;
export const SNAP_THRESHOLD = 10; // Threshold in pixels to consider a video "snapped" into view

/**
 * VerticalSnapScroller component renders a vertical scrolling container
 * that snaps to each video item as it comes into view.
 *
 * We will only enable scroll if we believe the user is on a touch supported mobile or tablet device.
 *
 * @param {object} props - The component props.
 * @param {number} props.activeVideoIndex - The index of the currently active video.
 * @param {boolean} props.isTouchSupported - Indicates if the device is a touch-capable mobile or tablet.
 * @param {React.ReactNode} props.videoComponent - The main video component to display.
 * @param {Array} props.videoItems - The video data objects.
 * @param {Function} [props.onActiveChange] - Optional callback when the active index changes.
 */
export function VerticalSnapScroller({
  activeVideoIndex,
  isTouchSupported,
  videoComponent,
  videoItems = [],
  onActiveChange,
}) {
  const containerRef = useRef(null);

  // On active video change scroll to the active video (middle) child
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const activeChild = container.children[ACTIVE_VIDEO_INDEX];
    if (activeChild) {
      activeChild.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  }, [activeVideoIndex]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let lastScrollTop = container.scrollTop;
    let frame;

    /**
     * Function that determines if the scroll has ended and checks if there is a new scroll target based
     * on the top position of the video thumbnails.
     */
    const checkForNewScrollTarget = () => {
      const currentScrollTop = container.scrollTop;
      const scrollHasEnded = currentScrollTop === lastScrollTop;
      if (scrollHasEnded) {
        const { children } = container;
        const containerTop = container.getBoundingClientRect().top;
        const previousThumbnailTopPosition = Math.abs(
          children[0].getBoundingClientRect().top - containerTop,
        );
        const nextThumbnailTopPosition = Math.abs(
          children[2].getBoundingClientRect().top - containerTop,
        );

        if (previousThumbnailTopPosition < SNAP_THRESHOLD) {
          onActiveChange('previous');
        } else if (nextThumbnailTopPosition < SNAP_THRESHOLD) {
          onActiveChange('next');
        }

        return;
      }

      lastScrollTop = currentScrollTop;
      frame = requestAnimationFrame(checkForNewScrollTarget);
    };

    /**
     * Function that handles the scroll event.
     * It uses requestAnimationFrame to avoid excessive calculations.
     */
    const handleScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(checkForNewScrollTarget);
    };

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(frame);
    };
  }, [onActiveChange]);

  if (videoItems.length === 0) {
    return null;
  }

  const previousIndex = (activeVideoIndex - 1 + videoItems.length) % videoItems.length;
  const nextIndex = (activeVideoIndex + 1) % videoItems.length;

  return (
    <div
      className={classNames(styles.snapScrollContainer, { [styles.touch]: isTouchSupported })}
      ref={containerRef}
    >
      <div
        className={classNames(styles.previousThumbnail, { [styles.none]: !isTouchSupported })}
        aria-label="Previous video thumbnail"
        data-index="previous"
      >
        <VideoThumbnail video={videoItems[previousIndex]} />
      </div>
      <div
        aria-label="Video player container"
        data-index="current"
      >
        {videoComponent}
      </div>
      <div
        className={classNames(styles.nextThumbnail, { [styles.none]: !isTouchSupported })}
        aria-label="Next video thumbnail"
        data-index="next"
      >
        <VideoThumbnail video={videoItems[nextIndex]} />
      </div>
    </div>
  );
}
